import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { TaskService } from '../../../modules/project-management/services/task.service';

/** Au-delà, le décompte exact n'apporte plus rien : « plus de 20 » suffit à alerter. */
const COUNT_CEILING = 20;

/**
 * Accroche affichée à l'ouverture de l'assistant sur une conversation vide
 * (MOKA-208 P4).
 *
 * Objectif : remplacer un écran qui attend une commande par un écran qui dit
 * quelque chose d'utile. Un assistant qui commence par « vous avez 3 tâches en
 * retard » se fait ouvrir une deuxième fois ; un assistant muet, non.
 *
 * Deux règles :
 * 1. **Jamais bloquant.** Le résumé s'affiche s'il arrive, la saisie n'attend
 *    pas après lui et une erreur reste silencieuse.
 * 2. **Rien à dire vaut mieux qu'une banalité.** Sans tâche en retard, aucun
 *    message : féliciter quelqu'un d'avoir zéro retard à chaque ouverture
 *    devient vite du bruit.
 */
@Injectable({ providedIn: 'root' })
export class VoiceBriefingService {
  private auth = inject(AuthService);
  private taskService = inject(TaskService);

  /** Phrase d'accroche, ou `null` s'il n'y a rien de notable à signaler. */
  readonly briefing = signal<string | null>(null);

  /** Évite de relancer la requête à chaque ouverture de l'overlay. */
  #loadedFor: string | null = null;

  async load(): Promise<void> {
    const profile = this.auth.profile;
    if (!profile?.id) return;
    if (this.#loadedFor === profile.id) return;
    this.#loadedFor = profile.id;

    try {
      const { total } = await firstValueFrom(
        this.taskService.getMyTasksPaginated(profile.id, 0, 1, { due: 'overdue' })
      );
      this.briefing.set(this.#format(total));
    } catch {
      // Silencieux : une accroche manquante ne doit pas se remarquer.
      this.briefing.set(null);
    }
  }

  /** À appeler au changement de profil : le résumé du précédent n'a plus de sens. */
  reset(): void {
    this.#loadedFor = null;
    this.briefing.set(null);
  }

  #format(overdue: number): string | null {
    if (overdue <= 0) return null;
    if (overdue === 1) return 'Vous avez 1 tâche en retard.';
    if (overdue > COUNT_CEILING) return `Vous avez plus de ${COUNT_CEILING} tâches en retard.`;
    return `Vous avez ${overdue} tâches en retard.`;
  }
}
